import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { BusinessAddress } from '@models/businesses';
import { Repo } from '@repos/repo.service';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { CollectionPaths } from '@models/collections';
import { Observable, Subject } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { UserProfileState } from '@state/user-profile';

@Injectable({
	providedIn: 'root',
})
export class BusinessAddressesService extends Repo<BusinessAddress, unknown> {
	@SelectSnapshot(UserProfileState.getCurrentBusinessId) businessId: string;

	create = (data: BusinessAddress): Observable<BusinessAddress> => {
		const createdAddress = new Subject<BusinessAddress>();
		this.All()
			.pipe(
				take(1),
				map((addresses) =>
					addresses.map((address) => address as unknown as BusinessAddress),
				),
			)
			.subscribe((addresses) => {
				if (addresses.length) {
					if (data.primary) {
						// this is the new primary location
						addresses
							.filter((address) => address.id !== data.id) // in case the observable updates while in progress
							.filter((address) => address.primary) // ignore non-primary addresses
							.forEach((address) =>
								this.Update(address.id, {
									// remove `primary` status from existing primary address(es)
									...address,
									primary: false,
								}),
							);
					}
				} else {
					// this is the first location and should be primary
					data.primary = true;
				}

				this.Create(data, null)
					.then((address) => {
						if (address) {
							createdAddress.next(address);
						} else {
							createdAddress.next(data);
						}
					})
					.finally(() => createdAddress.complete());
			});

		return createdAddress;
	};

	update = (uid: string, data: Partial<BusinessAddress>): Observable<void> => {
		const updateAddress = new Subject<void>();
		if (data.primary) {
			this.All()
				.pipe(
					take(1),
					map((addresses) =>
						addresses.map((address) => address as unknown as BusinessAddress),
					),
				)
				.subscribe((addresses) => {
					addresses
						.filter((address) => address.id !== uid) // in case the observable updates while in progress
						.filter((address) => address.primary) // ignore non-primary addresses
						.forEach((address) =>
							this.Update(address.id, {
								// remove `primary` status from existing primary address(es)
								...address,
								primary: false,
							}),
						);
				});
		}
		this.Update(uid, data)
			.then(() => updateAddress.next())
			.finally(() => updateAddress.complete());

		return updateAddress;
	};

	GetCollectionPath(businessId: string): string {
		return CollectionPaths.business_addresses(businessId ?? this.businessId);
	}

	constructor(private firestore: AngularFirestore) {
		super(firestore);
	}
}
